import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Coins, Landmark, Compass, Layers, Cpu, Gauge, LineChart, Fingerprint, ShieldCheck, Hourglass, ArrowRight } from "lucide-react";
import { getBrands, getSettings } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { FeatureGrid } from "@/components/site/FeatureGrid";
import { CompanyCard } from "@/components/site/CompanyCard";
import { GroupStructureDiagram } from "@/components/site/GroupStructureDiagram";
import { HomeNews } from "@/components/site/HomeNews";

const ROLE_ITEMS = [
  { title: "Capital Allocation", text: "Capital is committed with discipline and a long horizon, not chased toward trends.", icon: Coins },
  { title: "Governance", text: "Shared standards for ethics, compliance, and accountable decision-making.", icon: Landmark },
  { title: "Strategic Direction", text: "Clear mandates that keep each venture aligned to purpose over time.", icon: Compass },
  { title: "Shared Services", text: "Common systems and support that let operators focus on execution.", icon: Layers },
  { title: "Technology Enablement", text: "Technology applied with purpose, in service of durable operations.", icon: Cpu },
  { title: "Operational Excellence", text: "Consistent standards and rigour across every operating company.", icon: Gauge },
  { title: "Financial Oversight", text: "Consolidated controls, reporting, and prudent financial management.", icon: LineChart },
  { title: "Brand Independence", text: "Each brand retains the autonomy required to execute in its domain.", icon: Fingerprint },
  { title: "Risk Management", text: "Deliberate identification and stewardship of operational and financial risk.", icon: ShieldCheck },
  { title: "Long-term Stewardship", text: "Preserving intent and institutional knowledge across decades.", icon: Hourglass },
];

const PHILOSOPHY = [
  { title: "Purpose", text: "Every company must solve a meaningful real-world problem and exist for a clear reason." },
  { title: "Execution", text: "Ideas have no value without disciplined execution, operational excellence, and responsible governance." },
  { title: "Longevity", text: "We build businesses designed to endure for decades, not trends designed to last a season." },
];

const PRINCIPLES = [
  "Long-term thinking", "Real-world businesses", "Disciplined execution", "Responsible growth",
  "Technology with purpose", "Capital discipline", "Institutional trust", "Sustainability through execution",
];

export default function Home() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: brands = [], isLoading } = useQuery({ queryKey: ["brands"], queryFn: getBrands });

  const featuredList = brands.filter((b) => b.featured);
  const featured = (featuredList.length > 0 ? featuredList : brands).slice(0, 3);

  return (
    <>
      <Seo path="/" settings={settings} />

      {/* HERO */}
      <section className="hero-gradient border-b" style={{ borderColor: "var(--card-border)" }}>
        <div className="container-inst pt-20 pb-20 sm:pt-24 lg:pt-28 lg:pb-24">
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <Reveal>
              <img src="/brand/logo-full.webp" alt="Acharya Holdings" className="mx-auto w-full max-w-[360px] sm:max-w-[400px]" style={{ opacity: 0.95 }} />
            </Reveal>
            <Reveal delay={0.05}>
              <div className="bronze-rule mx-auto mt-6" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="motif-caps mt-6">Purpose &bull; Execution &bull; Longevity</p>
            </Reveal>
            <Reveal delay={0.15}>
              <h1 data-testid="home-hero-headline" className="mt-8 max-w-[20ch] text-[28px] leading-[1.25] sm:text-[34px] lg:text-[40px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.2px" }}>
                Building enduring businesses.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-[68ch] text-[16px] leading-[1.85] sm:text-[17px]" style={{ color: "var(--muted)", letterSpacing: "0.2px" }}>
                Acharya Holdings is an independent venture house focused on building and operating long-term
                businesses across physical infrastructure, consumer products, public initiatives, and media. We
                work at the intersection of real-world systems, institutional trust, and durable execution.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
                <Link to="/companies" data-testid="home-hero-secondary-cta" className="btn btn-primary">
                  Our Companies <ArrowRight size={15} />
                </Link>
                <Link to="/contact" data-testid="home-hero-primary-cta" className="btn btn-secondary">
                  Contact Us <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO WE ARE + OUR ROLE — warm light editorial expanse */}
      <div className="surface-cream">
      <section className="container-inst pt-20 pb-16 lg:pt-28 lg:pb-20" data-testid="who-we-are-section">
        <SectionHeading title="Who We Are"
          description="Founded in India, Acharya Holdings operates as a parent platform for multiple purpose-driven ventures addressing distinct real-world problem spaces. Each initiative is built with a clear operational mandate rather than speculative growth." />
        <div className="mt-14 space-y-12">
          {[
            { h: "Why we exist", p: "To provide continuity across ventures that operate in fundamentally different domains but share a common philosophy of long-term value creation." },
            { h: "How we operate", p: "We maintain consolidated oversight of governance, capital discipline, and risk, while allowing each brand the autonomy required to execute effectively within its own domain." },
            { h: "Why independence matters", p: "Each venture is structured to evolve into a standalone entity when operational maturity, regulatory considerations, or strategic direction make separation appropriate." },
          ].map((c, i) => (
            <Reveal key={c.h} delay={i * 0.04}>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[260px_1fr] lg:gap-12">
                <h3 className="text-[18px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.2px" }}>{c.h}</h3>
                <p className="max-w-[64ch] text-[15px] leading-[1.85]" style={{ color: "var(--muted)" }}>{c.p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUR ROLE */}
      <section className="container-inst pb-20 lg:pb-28" data-testid="our-role-section">
        <SectionHeading title="Our Role"
          description="Acharya Holdings functions not merely as an investor, but as a permanent institutional platform supporting multi-decade ventures." />
        <div className="mt-14">
          <FeatureGrid items={ROLE_ITEMS} />
        </div>
      </section>

      </div>

      {/* OUR COMPANIES + GROUP STRUCTURE — deep charcoal */}
      <div className="surface-dark">
      <section id="companies" className="container-inst pt-20 pb-20 lg:pt-28 lg:pb-28" data-testid="our-companies-section">
        <SectionHeading title="Our Companies"
          description="A selection of the operating companies within the group. Each brand operates independently, supported by shared governance, standards, and long-term intent." />
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-64 animate-pulse rounded-[18px]" style={{ background: "var(--surface)", border: "1px solid var(--card-border)" }} />
              ))
            : featured.map((b) => <CompanyCard key={b.id} brand={b} />)}
        </div>
        <div className="mt-12">
          <Link to="/companies" data-testid="home-view-all-companies" className="btn btn-secondary">
            View All Companies <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* GROUP STRUCTURE */}
      <section className="container-inst pb-20 lg:pb-28" data-testid="group-structure-section">
        <SectionHeading title="Group Structure"
          description="A clear flow from governance and capital through to independent operating companies and long-term value creation." />
        <div className="mt-14">
          <GroupStructureDiagram />
        </div>
      </section>

      </div>

      {/* NEWS (homepage) — shown only when enabled in the CMS */}
      <HomeNews />

      {/* PHILOSOPHY — light stone close */}
      <div className="surface-stone">
      <section className="container-inst pt-20 pb-24 lg:pt-28 lg:pb-32" data-testid="philosophy-section">
        <SectionHeading title="Our Philosophy"
          description="These are not marketing slogans. They are the principles behind every venture we build \u2014 Purpose, Execution, and Longevity." />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PHILOSOPHY.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.04}>
              <div className="h-full rounded-[18px] p-8" style={{ background: "var(--surface)", border: "1px solid var(--card-border)" }}>
                <p style={{ color: "var(--accent)", fontSize: 12, letterSpacing: "0.2em" }}>{`0${i + 1}`}</p>
                <h3 className="mt-5 text-[20px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.2px" }}>{p.title}</h3>
                <p className="mt-3 text-[14px] leading-[1.85]" style={{ color: "var(--muted)" }}>{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap gap-2.5">
          {PRINCIPLES.map((pr) => (
            <span key={pr} className="rounded-full px-4 py-1.5 text-[12px]" style={{ border: "1px solid var(--card-border)", color: "var(--muted)" }}>{pr}</span>
          ))}
        </div>
      </section>
      </div>
    </>
  );
}
